// frontend/src/components/Diario/IndicadorProgressoTarefas.jsx
// Componente que exibe o percentual concluído das tarefas de forma destacada

import React, { useMemo } from 'react';
import { TrendingUp, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { parseLocalDateTime } from '../../utils/formatters';

/**
 * Componente de Indicador de Progresso de Tarefas
 * Exibe o percentual concluído de forma visual e destacada
 * 
 * @param {array} tarefas - Lista de todas as tarefas (pendentes e concluídas)
 */
const IndicadorProgressoTarefas = ({ tarefas = [] }) => {
    // Calcula estatísticas das tarefas
    const estatisticas = useMemo(() => {
        const agora = new Date();
        
        const tarefasConcluidas = tarefas.filter(t => t.status === 'Concluída');
        // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
        const tarefasAtrasadas = tarefas.filter(t => {
            if (t.status === 'Atrasada') return true;
            if (!t.hora_agendada) return false;
            const horaAgendada = parseLocalDateTime(t.hora_agendada);
            if (!horaAgendada) return false;
            const diferencaMinutos = (agora - horaAgendada) / (1000 * 60);
            return diferencaMinutos > 15 && (t.status === 'Pendente' || !t.status);
        });
        const tarefasPendentes = tarefas.filter(t => {
            if (t.status === 'Concluída') return false;
            if (t.status === 'Atrasada') return false;
            if (!t.hora_agendada) return true;
            const horaAgendada = parseLocalDateTime(t.hora_agendada);
            if (!horaAgendada) return true;
            const diferencaMinutos = (agora - horaAgendada) / (1000 * 60);
            return diferencaMinutos <= 15;
        });

        const totalTarefas = tarefas.length;
        const concluidasCount = tarefasConcluidas.length;
        const atrasadasCount = tarefasAtrasadas.length;
        const pendentesCount = tarefasPendentes.length;

        // Calcula percentual concluído
        const percentualConcluido = totalTarefas > 0 && concluidasCount > 0
            ? Math.round((concluidasCount / totalTarefas) * 100)
            : 0;

        return {
            total: totalTarefas,
            concluidas: concluidasCount,
            atrasadas: atrasadasCount,
            pendentes: pendentesCount,
            percentual: percentualConcluido
        };
    }, [tarefas]);

    // Se não houver tarefas, não exibe nada
    if (estatisticas.total === 0) {
        return null;
    }

    // Determina a cor baseada no percentual
    const getCorProgresso = (percentual) => {
        if (percentual >= 80) return 'bg-emerald-500';
        if (percentual >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const corProgresso = getCorProgresso(estatisticas.percentual);

    return (
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl shadow-md border border-emerald-100 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-white rounded-full shadow-sm">
                        <TrendingUp size={24} className="text-emerald-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">
                            Progresso do Plantão
                        </h3>
                        <p className="text-sm text-gray-600">
                            {estatisticas.concluidas} de {estatisticas.total} tarefas concluídas
                        </p>
                    </div>
                </div>
                
                {/* Percentual em destaque */}
                <div className="text-right">
                    <div className="text-4xl font-bold text-emerald-700">
                        {estatisticas.percentual}%
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Concluído</p>
                </div>
            </div>

            {/* Barra de Progresso */}
            <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                    <div
                        className={`${corProgresso} h-full transition-all duration-700 ease-out rounded-full flex items-center justify-end pr-2`}
                        style={{ width: `${estatisticas.percentual}%` }}
                    >
                        {estatisticas.percentual > 15 && (
                            <span className="text-xs font-bold text-white">
                                {estatisticas.percentual}%
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Estatísticas Detalhadas */}
            <div className="grid grid-cols-3 gap-3">
                {/* Tarefas Concluídas */}
                <div className="bg-white rounded-lg p-3 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <span className="text-xs font-medium text-gray-600">Concluídas</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-700">
                        {estatisticas.concluidas}
                    </div>
                </div>

                {/* Tarefas Pendentes */}
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                        <Clock size={16} className="text-blue-600" />
                        <span className="text-xs font-medium text-gray-600">Pendentes</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-700">
                        {estatisticas.pendentes}
                    </div>
                </div>

                {/* Tarefas Atrasadas */}
                <div className="bg-white rounded-lg p-3 border border-red-100">
                    <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle size={16} className="text-red-600" />
                        <span className="text-xs font-medium text-gray-600">Atrasadas</span>
                    </div>
                    <div className="text-2xl font-bold text-red-700">
                        {estatisticas.atrasadas}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndicadorProgressoTarefas;

